// ObjectEditor.jsx - Form for creating/editing museum objects
import React, { useState, useEffect } from 'react';
import { X, Save, Hash, Image, FileText, MapPin, Tag, User, Clock, Ruler, Archive, Eye, Star, AlertCircle } from 'lucide-react';
import ImageUploader from '../ItineraryCreator/ui/ImageUploader';

const ObjectEditor = ({
  object,
  museum,
  isOpen,
  onClose,
  onSave,
  isLoading = false,
  getNextNumber,
  checkNumberAvailable
}) => {
  const [formData, setFormData] = useState({
    objectNumber: 1,
    name: '',
    description: '',
    photoUrl: '',
    room: '',
    floor: 1,
    category: '',
    artist: '',
    period: '',
    materials: [],
    dimensions: '',
    inventoryNumber: '',
    featured: false,
    visible: true
  });

  const [errors, setErrors] = useState({});
  const [materialsInput, setMaterialsInput] = useState('');
  const [numberError, setNumberError] = useState('');

  const isEditMode = !!object?.id;

  // Initialize form when object changes
  useEffect(() => {
    const initForm = async () => {
      if (object) {
        setFormData({
          objectNumber: object.objectNumber || 1,
          name: object.name || '',
          description: object.description || '',
          photoUrl: object.photoUrl || '',
          room: object.room || '',
          floor: object.floor || 1,
          category: object.category || '',
          artist: object.artist || '',
          period: object.period || '',
          materials: object.materials || [],
          dimensions: object.dimensions || '',
          inventoryNumber: object.inventoryNumber || '',
          featured: object.featured || false,
          visible: object.visible !== false
        });
        setMaterialsInput((object.materials || []).join(', '));
      } else if (getNextNumber) {
        // New object - get next number
        const nextNum = await getNextNumber();
        setFormData(prev => ({ ...prev, objectNumber: nextNum }));
      }
    };

    initForm();
    setErrors({});
    setNumberError('');
  }, [object, getNextNumber]);

  // Handle field changes
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Handle object number change with validation
  const handleNumberChange = async (value) => {
    const num = parseInt(value) || 0;
    handleChange('objectNumber', num);
    setNumberError('');

    if (num > 0 && checkNumberAvailable) {
      const available = await checkNumberAvailable(num, object?.id);
      if (!available) {
        setNumberError(`Number ${num} is already in use`);
      }
    }
  };

  // Handle materials input
  const handleMaterialsChange = (value) => {
    setMaterialsInput(value);
    const materials = value.split(',').map(m => m.trim()).filter(m => m);
    handleChange('materials', materials);
  };

  // Handle image upload
  const handleImageUploaded = (url) => {
    handleChange('photoUrl', url);
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.objectNumber || formData.objectNumber < 1) {
      newErrors.objectNumber = 'Valid object number is required';
    }

    if (numberError) {
      newErrors.objectNumber = numberError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle save
  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      await onSave(formData);
      onClose();
    } catch (err) {
      console.error('Failed to save object:', err);
      setErrors({ submit: err.message });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-gray-50">
          <h2 className="text-xl font-semibold text-gray-800">
            {isEditMode ? 'Edit Object' : 'Add New Object'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 p-1"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Object Number */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
              <Hash className="w-4 h-4" />
              Object Number *
            </label>
            <input
              type="number"
              min="1"
              value={formData.objectNumber}
              onChange={(e) => handleNumberChange(e.target.value)}
              className={`w-32 p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.objectNumber || numberError ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {(errors.objectNumber || numberError) && (
              <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.objectNumber || numberError}
              </p>
            )}
            <p className="text-gray-500 text-xs mt-1">
              This is the number displayed on the physical tag
            </p>
          </div>

          {/* Name */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
              <FileText className="w-4 h-4" />
              Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.name ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Object name"
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
              <FileText className="w-4 h-4" />
              Description *
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={3}
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.description ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Brief description of the object..."
            />
            {errors.description && (
              <p className="text-red-500 text-sm mt-1">{errors.description}</p>
            )}
          </div>

          {/* Photo */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
              <Image className="w-4 h-4" />
              Photo
            </label>
            <ImageUploader
              imageUrl={formData.photoUrl}
              onImageUploaded={handleImageUploaded}
              itineraryMetadata={{
                country: 'museums',
                name: museum?.name || 'museum',
                objectNumber: formData.objectNumber
              }}
              label=""
              previewSize="h-48"
              maxSizeKB={400}
            />
          </div>

          {/* Room & Floor */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                <MapPin className="w-4 h-4" />
                Room
              </label>
              <select
                value={formData.room}
                onChange={(e) => {
                  const selectedRoom = museum?.rooms?.find(r => r.id === e.target.value);
                  handleChange('room', e.target.value);
                  if (selectedRoom) {
                    handleChange('floor', selectedRoom.floor);
                  }
                }}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select room...</option>
                {museum?.rooms?.map(room => (
                  <option key={room.id} value={room.id}>
                    {room.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                Floor
              </label>
              <select
                value={formData.floor}
                onChange={(e) => handleChange('floor', parseInt(e.target.value))}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {[-1, 0, 1, 2, 3, 4, 5].map(floor => (
                  <option key={floor} value={floor}>
                    {floor === -1 ? 'Basement' : floor === 0 ? 'Ground Floor' : `Floor ${floor}`}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Category */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
              <Tag className="w-4 h-4" />
              Category
            </label>
            <select
              value={formData.category}
              onChange={(e) => handleChange('category', e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select category...</option>
              {museum?.categories?.map(cat => (
                <option key={cat} value={cat}>
                  {cat.charAt(0).toUpperCase() + cat.slice(1)}
                </option>
              ))}
            </select>
          </div>

          {/* Optional Details Section */}
          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-sm font-medium text-gray-700 mb-4">Optional Details</h3>

            <div className="space-y-4">
              {/* Artist */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                  <User className="w-4 h-4" />
                  Artist / Creator
                </label>
                <input
                  type="text"
                  value={formData.artist}
                  onChange={(e) => handleChange('artist', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Artist name"
                />
              </div>

              {/* Period */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                  <Clock className="w-4 h-4" />
                  Period / Date
                </label>
                <input
                  type="text"
                  value={formData.period}
                  onChange={(e) => handleChange('period', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., 3rd century BC, Renaissance, 1850"
                />
              </div>

              {/* Materials */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                  Materials
                </label>
                <input
                  type="text"
                  value={materialsInput}
                  onChange={(e) => handleMaterialsChange(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., bronze, marble, gold (comma-separated)"
                />
              </div>

              {/* Dimensions */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                  <Ruler className="w-4 h-4" />
                  Dimensions
                </label>
                <input
                  type="text"
                  value={formData.dimensions}
                  onChange={(e) => handleChange('dimensions', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., 12cm x 8cm x 5cm"
                />
              </div>

              {/* Inventory Number */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                  <Archive className="w-4 h-4" />
                  Inventory Number
                </label>
                <input
                  type="text"
                  value={formData.inventoryNumber}
                  onChange={(e) => handleChange('inventoryNumber', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Museum catalog number"
                />
              </div>
            </div>
          </div>

          {/* Display Options */}
          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-sm font-medium text-gray-700 mb-4">Display Options</h3>

            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.visible}
                  onChange={(e) => handleChange('visible', e.target.checked)}
                  className="w-5 h-5 rounded border-gray-300 text-blue-500 focus:ring-blue-500"
                />
                <Eye className="w-4 h-4 text-gray-500" />
                <span className="text-gray-700">Visible to visitors</span>
              </label>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.featured}
                  onChange={(e) => handleChange('featured', e.target.checked)}
                  className="w-5 h-5 rounded border-gray-300 text-yellow-500 focus:ring-yellow-500"
                />
                <Star className="w-4 h-4 text-gray-500" />
                <span className="text-gray-700">Featured object</span>
              </label>
            </div>
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-600 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {errors.submit}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading || numberError}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {isLoading ? 'Saving...' : isEditMode ? 'Save Changes' : 'Add Object'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ObjectEditor;
